/**
 * StatCard — стат-карточка верхних плашек разделов («Аналитика», «Проекты»):
 * заголовок + чип динамики, крупное значение, единица, строки-футеры.
 *
 * Значение «—» (или null) = данных с бэка нет: карточка показывает только
 * название и серую подпись «нет данных» — без чипа динамики, единицы и
 * футеров (замечание ревью от 14.08.2026).
 *
 * Низ карточки — три фиксированных строки (значение / единица / футер),
 * прижатых к низу через mt-auto: единица и футер резервируют высоту даже
 * пустыми, поэтому по всем карточкам ряда значения, единицы и футеры
 * лежат на одних линиях независимо от высоты заголовка и наличия чипа.
 * danger — красное значение (например, отток миграции).
 */
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

export interface StatCardTrend {
  /** Текст чипа как в макете («+1.2%», «-1.2%», «+1.8»). */
  text: string;
  /** Стрелка вниз (чип остаётся зелёным). */
  down?: boolean;
}

export interface StatCardFootnote {
  text: string;
  /** Жирная тёмная строка («1,82 млн. тенге»). */
  bold?: boolean;
  /** Красная строка («отток (к 05.05.2026)»). */
  danger?: boolean;
}

interface Props {
  title: string;
  /** «—» или null → режим «нет данных» (единица/тренд/футеры скрыты). */
  value: string | null;
  unit?: string | null;
  /** Красное значение. */
  danger?: boolean;
  trend?: StatCardTrend | null;
  footnotes?: StatCardFootnote[];
  testId: string;
}

export const StatCard = ({
  title,
  value,
  unit,
  danger = false,
  trend,
  footnotes = [],
  testId,
}: Props) => {
  const { t } = useTranslation();
  const noData = value === null || value === '—';
  const TrendIcon = trend?.down ? TrendingDown : TrendingUp;

  if (noData) {
    return (
      <div
        data-testid={testId}
        className="flex h-full min-h-[10rem] flex-col rounded-2xl border border-surface-border bg-gradient-to-b from-surface to-surface-page p-5 shadow-card"
      >
        <h3 className="min-w-0 text-sm font-extrabold leading-snug">
          {title}
        </h3>
        <p
          className="mt-auto pb-5 text-sm lowercase text-text-muted"
          data-testid={`${testId}-nodata`}
        >
          {t('common.noData')}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className="flex h-full min-h-[10rem] flex-col rounded-2xl border border-surface-border bg-gradient-to-b from-surface to-surface-page p-5 shadow-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="min-w-0 text-sm font-extrabold leading-snug">{title}</h3>
        {trend && (
          <span
            data-testid={`${testId}-trend`}
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-brand-100 bg-surface px-3 py-1 text-sm font-bold text-brand-600"
          >
            <TrendIcon className="size-4" />
            {trend.text}
          </span>
        )}
      </div>

      {/* Значение — одна строка, прижатая к низу карточки. */}
      <p className="mt-auto whitespace-nowrap pt-3">
        <span
          data-testid={`${testId}-value`}
          className={cn(
            'text-3xl font-extrabold tabular-nums leading-tight',
            danger ? 'text-status-danger' : 'text-chart-indigo',
          )}
        >
          {value}
        </span>
      </p>

      {/* Единица — всегда своя строка с резервом высоты, чтобы «человек»
          у соседних карточек лежал на одной линии. */}
      <p className="mt-1 min-h-5 truncate text-sm text-text-muted">
        {unit ?? ' '}
      </p>

      {/* Блок футеров рендерится всегда (min-h на одну строку): без него
          значение карточки без футера опускается ниже соседних. */}
      <div className="mt-2 min-h-5 space-y-0.5">
        {footnotes.map((note) => (
          <p
            key={note.text}
            className={cn(
              'text-[13px]',
              note.danger
                ? 'text-status-danger'
                : note.bold
                  ? 'font-bold text-text'
                  : 'text-text-muted',
            )}
          >
            {note.text}
          </p>
        ))}
      </div>
    </div>
  );
};
